import { formatDate, todayIST } from "@templeos/shared";
import { useEffect, useState } from "react";
import { useNow } from "../components/hooks.js";
import { api, errorText } from "../lib/api.js";
import { istMinutes } from "../lib/cart.js";

interface CatalogRow { id: string; kind: string; name: string; name_hi: string | null; requires_date: boolean; daily_capacity: number | null; slots: string[] }
interface Slot { slot: string; capacity: number; booked: number; left: number }
interface BoardRow { item: CatalogRow; slots: Slot[] }

const REFRESH_MS = 30_000;
const endMin = (slot: string) => { const [h, m] = (slot.split("-")[1] ?? "00:00").split(":").map(Number); return (h ?? 0) * 60 + (m ?? 0); };

function SlotRow({ s, over }: { s: Slot; over: boolean }) {
  const pct = s.capacity ? Math.min(100, Math.round((s.booked / s.capacity) * 100)) : 0;
  const full = s.left <= 0;
  const low = !full && s.left / Math.max(1, s.capacity) < 0.2;
  const status = over ? "Closed" : full ? "Full" : `${s.left} left`;
  return (
    <div className="pos-tv-slot" style={over ? { opacity: 0.45 } : undefined}>
      <span className="tos-num">{s.slot || "All day"}</span>
      <span className="pos-tv-bar" role="img" aria-label={`${pct}% booked`}><i className={full ? "full" : low ? "low" : ""} style={{ width: `${pct}%` }} /></span>
      <span className="pos-tv-left">{status}</span>
    </div>
  );
}

/** TV board: today's darshan and seva slot availability, readable across the hall; refreshes every 30 seconds. */
export function Board() {
  const now = useNow(1_000);
  const [templeName, setTempleName] = useState("Temple");
  const [rows, setRows] = useState<BoardRow[] | null>(null);
  const [updated, setUpdated] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const day = todayIST();
        const [profile, catalog] = await Promise.all([
          api.get<{ name: string } | null>("/api/public/profile"),
          api.get<CatalogRow[]>("/api/public/catalog"),
        ]);
        const dated = catalog.filter((i) => i.requires_date && i.daily_capacity);
        const avail = await Promise.all(dated.map((i) => api.get<{ slots: Slot[] }>(`/api/public/catalog/${i.id}/availability?day=${day}`)));
        if (!alive) return;
        if (profile?.name) setTempleName(profile.name);
        setRows(dated.map((item, k) => ({ item, slots: avail[k]?.slots ?? [] })));
        setUpdated(new Date());
        setError(null);
      } catch (e) {
        if (alive) setError(errorText(e));
      }
    };
    void load();
    const iv = setInterval(load, REFRESH_MS);
    return () => { alive = false; clearInterval(iv); };
  }, []);

  useEffect(() => { document.title = `${templeName} · Darshan board`; }, [templeName]);
  const nowMin = istMinutes(new Date(now));
  const section = (title: string, kinds: string[]) => {
    const list = (rows ?? []).filter((r) => kinds.includes(r.item.kind));
    if (!list.length) return null;
    return (
      <section aria-label={title}>
        <h2>{title}</h2>
        <div className="pos-tv-grid">
          {list.map((r) => (
            <article key={r.item.id} className="pos-tv-card">
              <h3>{r.item.name}{r.item.name_hi && <small lang="hi">{r.item.name_hi}</small>}</h3>
              {r.slots.map((sl) => <SlotRow key={sl.slot} s={sl} over={Boolean(sl.slot) && endMin(sl.slot) <= nowMin} />)}
            </article>
          ))}
        </div>
      </section>
    );
  };

  return (
    <div className="tos-root pos-tv">
      <header className="pos-tv-head">
        <h1>{templeName}: today's darshan & seva</h1>
        <div className="pos-tv-clock" aria-label="Current time">{new Date(now).toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit" })}</div>
      </header>
      {rows === null && !error && <div style={{ fontSize: "2vw" }}>Loading availability…</div>}
      {rows !== null && !rows.length && <div style={{ fontSize: "2vw" }}>No slot-based darshan or seva is open for booking today.</div>}
      {section("Darshan", ["ticket"])}
      {section("Seva & pooja", ["seva", "other", "prasad"])}
      <footer className="pos-tv-foot">
        <span>{formatDate(todayIST())}</span>
        <span role="status">{error ? `Connection problem, showing last data (${error})` : updated ? `Updated ${updated.toLocaleTimeString("en-IN")} · refreshes every 30 seconds` : ""}</span>
      </footer>
    </div>
  );
}
